/**
 * Kampagnen-Herkunft eines Leads.
 *
 * Hintergrund: Die Lead-Mail enthielt bisher nur, was im Formular stand. Ob
 * eine Anfrage aus einer Anzeige, einer KI-Antwort oder einer Empfehlung kam,
 * liess sich nicht nachvollziehen, und ohne gclid ist kein Offline-Import
 * qualifizierter Leads nach Google Ads moeglich.
 *
 * Datenschutz: Gespeichert (sessionStorage) wird nur bei erteilter
 * CookieYes-Kategorie "advertisement". Ohne Einwilligung liest getAttribution()
 * ausschliesslich die Parameter der aktuell geoeffneten URL und den Host der
 * verweisenden Seite, die ohnehin im Request stehen, und weder Cookie noch
 * Speicher.
 */

export const ATTRIBUTION_KEYS = [
  'gclid',
  'gbraid',
  'wbraid',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'landing_page',
  'referrer_host',
  'first_seen'
] as const

export type AttributionKey = (typeof ATTRIBUTION_KEYS)[number]
export type Attribution = Partial<Record<AttributionKey, string>>

const URL_KEYS = ['gclid', 'gbraid', 'wbraid', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const

export const ATTRIBUTION_STORAGE_KEY = 'kmrs_attr'
/** Obergrenze je Wert, deckungsgleich mit dem Schema der Serverroute. */
export const ATTRIBUTION_MAX_LENGTH = 300

type CookieYesConsent = { categories?: Record<string, boolean> }

const clip = (value: string) => value.slice(0, ATTRIBUTION_MAX_LENGTH)

function readCookie(name: string): string | null {
  try {
    const match = document.cookie.split('; ').find((part) => part.startsWith(`${name}=`))
    return match ? decodeURIComponent(match.slice(name.length + 1)) : null
  } catch {
    return null
  }
}

/**
 * Einwilligung in Werbe-Cookies laut CookieYes. Primaer ueber die offizielle
 * API getCkyConsent(), ersatzweise ueber das Consent-Cookie, falls das Skript
 * (noch) nicht geladen ist.
 */
export function hasAdConsent(): boolean {
  try {
    const getConsent = (window as Window & { getCkyConsent?: () => CookieYesConsent }).getCkyConsent
    if (typeof getConsent === 'function') {
      return getConsent()?.categories?.advertisement === true
    }
    const cookie = readCookie('cookieyes-consent')
    return cookie ? /(^|,)advertisement:yes(,|$)/.test(cookie) : false
  } catch {
    return false
  }
}

function fromUrl(): Attribution {
  const result: Attribution = {}
  try {
    const params = new URLSearchParams(window.location.search)
    for (const key of URL_KEYS) {
      const value = params.get(key)
      if (value) result[key] = clip(value)
    }
  } catch {
    // Ohne lesbare URL bleibt die Herkunft leer
  }
  return result
}

/** gclid aus dem Google-Ads-Cookie, Format 'GCL.<zeitstempel>.<gclid>'. */
function gclidFromCookie(): string | undefined {
  const value = readCookie('_gcl_aw')
  if (!value) return undefined
  const parts = value.split('.')
  return parts.length >= 3 && parts[2] ? clip(parts.slice(2).join('.')) : undefined
}

function readStored(): Attribution | null {
  try {
    const raw = window.sessionStorage.getItem(ATTRIBUTION_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Record<string, unknown>
    const result: Attribution = {}
    for (const key of ATTRIBUTION_KEYS) {
      const value = parsed[key]
      if (typeof value === 'string' && value) result[key] = clip(value)
    }
    return result
  } catch {
    return null
  }
}

/**
 * Host der verweisenden Seite (nur der Hostname, nie Pfad oder Query), wenn
 * er nicht die eigene Domain ist. document.referrer bleibt bei clientseitiger
 * Navigation auf dem urspruenglichen Wert, interne Wechsel fallen hier raus.
 */
function referrerHost(): string | undefined {
  try {
    const raw = document.referrer
    if (!raw) return undefined
    const host = new URL(raw).hostname
    if (!host || host === window.location.hostname) return undefined
    return clip(host)
  } catch {
    return undefined
  }
}

function currentPath(): string | undefined {
  try {
    return clip(window.location.pathname)
  } catch {
    return undefined
  }
}

/**
 * Haelt die Einstiegsseite und die Kampagnenparameter des ersten Seitenaufrufs
 * der Sitzung fest. Laeuft global (MailtoTracker) und nach einem
 * Consent-Update erneut; ein bestehender Eintrag wird nie ueberschrieben.
 */
export function captureAttribution(): void {
  try {
    if (!hasAdConsent()) return
    if (window.sessionStorage.getItem(ATTRIBUTION_STORAGE_KEY)) return
    const entry: Attribution = { ...fromUrl(), landing_page: currentPath(), first_seen: new Date().toISOString() }
    const referrer = referrerHost()
    if (referrer) entry.referrer_host = referrer
    if (!entry.gclid) {
      const cookieGclid = gclidFromCookie()
      if (cookieGclid) entry.gclid = cookieGclid
    }
    window.sessionStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(entry))
  } catch {
    // Speicher gesperrt (privater Modus): Formular nutzt dann nur die URL
  }
}

/**
 * Herkunft fuer das Formular. Parameter der aktuellen URL haben Vorrang, dann
 * der Sitzungseintrag, zuletzt das _gcl_aw-Cookie. Ohne Einwilligung nur die
 * aktuelle URL samt Pfad und der Host der verweisenden Seite (beides steht
 * ohnehin im Request).
 */
export function getAttribution(): Attribution {
  const current = fromUrl()
  const referrer = referrerHost()
  if (!hasAdConsent()) {
    const result: Attribution = { ...current }
    const path = currentPath()
    if (path) result.landing_page = path
    if (referrer) result.referrer_host = referrer
    return result
  }

  const stored = readStored() ?? {}
  // Kampagnenparameter nur als Satz uebernehmen, damit nie utm_source der
  // aktuellen URL mit utm_campaign eines frueheren Einstiegs vermischt wird.
  const result: Attribution = { landing_page: stored.landing_page, referrer_host: stored.referrer_host ?? referrer, first_seen: stored.first_seen }
  const campaign: Attribution = Object.keys(current).length > 0 ? current : stored
  for (const key of URL_KEYS) {
    if (campaign[key]) result[key] = campaign[key]
  }
  for (const key of ATTRIBUTION_KEYS) {
    if (!result[key]) delete result[key]
  }
  if (!result.landing_page) {
    const path = currentPath()
    if (path) result.landing_page = path
  }
  if (!result.gclid) {
    const cookieGclid = gclidFromCookie()
    if (cookieGclid) result.gclid = cookieGclid
  }
  return result
}
